/**
 * Holds API calls while the session is being re-established as the app starts.
 *
 * "Startup" here is the auth sense — restoring the user's session on load — not
 * react-bootstrap, the UI library this app renders with.
 *
 * Components fire requests as they mount. With a memory-only token there is
 * nothing to send until the silent refresh in AppLoadingContainer resolves, so
 * without this gate a reload would 401 in a herd; every one of those 401s would
 * queue behind the same refresh anyway, costing a wasted round trip each, and
 * any call whose retry gives up would surface as a spurious error.
 *
 * Deliberately separate from authStore: authStore owns the token, this owns
 * "is startup still running". It is also a leaf module, so the api layer can
 * wait on it without importing anything that imports the api layer back.
 *
 * PHASE 3 of the refresh-token removal (see docs/refresh-token-removal-plan.md,
 * where this sequence is called the bootstrap).
 */

/** How long session startup may run before we stop waiting on it. */
export const STARTUP_TIMEOUT_MS = 15000;

let sessionReady: Promise<void> | null = null;

/**
 * Route queued requests behind `work`.
 *
 * The gate itself never rejects: a request waits for startup to settle, and a
 * failed refresh is reported by the startup sequence that owns it, not by every
 * request that happened to be waiting.
 * @param {Promise} work the in-flight session startup
 * @returns {Promise} `work` itself, so the caller still sees its outcome
 */
export function gateSessionOn<T>(work: Promise<T>): Promise<T> {
  sessionReady = work.then(
    () => undefined,
    () => undefined,
  );

  return work;
}

/**
 * Wait for session startup, if it is still running.
 * @returns {Promise<void>} resolves once startup has settled, or immediately
 * when none is in flight
 */
export const whenSessionReady = (): Promise<void> =>
  sessionReady || Promise.resolve();

/**
 * Drop any gate left from an earlier run. Test seam; nothing in the app starts
 * the session twice.
 * @returns {void}
 */
export const resetSessionGate = (): void => {
  sessionReady = null;
};

/** Raised when session startup outlives STARTUP_TIMEOUT_MS. */
export class StartupTimeoutError extends Error {
  constructor(ms: number) {
    super(`Session startup did not settle within ${ms}ms`);
    this.name = 'StartupTimeoutError';
  }
}

/**
 * Reject if `work` has not settled in time, so a hung refresh cannot leave the
 * app sitting on its spinner for as long as the request timeout allows.
 * @param {Promise} work the startup sequence to bound
 * @param {Number} ms how long to wait
 * @returns {Promise} `work`'s result, or a StartupTimeoutError
 */
export function withStartupTimeout<T>(
  work: Promise<T>,
  ms: number = STARTUP_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const expiry = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new StartupTimeoutError(ms)), ms);
  });

  return Promise.race([work, expiry]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }) as Promise<T>;
}
